var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { autoUpdate, flip, FloatingFocusManager, FloatingList, offset as floatingOffset, size as floatingSize, useClick, useDismiss, useFloating, useInteractions, useListNavigation, useRole, } from '@floating-ui/react';
import { mergeRefs } from '@react-aria/utils';
import { cloneElement, useCallback, useEffect, useRef, useState, } from 'react';
import { mergeProps } from 'react-aria';
import { useId } from '../hooks';
import { useHasParentModal } from '../modal';
import { Portal } from '../portal';
import { tokens } from '../tokens';
import { DropdownProvider } from './dropdown-context';
import { DROPDOWN_MIN_WIDTH, dropdownStyle } from './styles';
// Mapping between Waffles and floating-ui placement
const placementMap = {
    bottom: 'bottom',
    bottomLeft: 'bottom-start',
    bottomRight: 'bottom-end',
    left: 'left-start',
    right: 'right-start',
    top: 'top',
    topLeft: 'top-start',
    topRight: 'top-end',
};
function DropdownInternal(_a) {
    var _b, _c;
    var { children, customPortalId, disableFirstItemAutoselect = false, disableOpenOnArrowKeyDown = false, fullWidth = false, inverted = false, isOpen, offset = tokens.spacingNew.xsmall, onClose, placement = 'bottomRight', role = 'menu', size = 'medium', trigger } = _a, restProps = __rest(_a, ["children", "customPortalId", "disableFirstItemAutoselect", "disableOpenOnArrowKeyDown", "fullWidth", "inverted", "isOpen", "offset", "onClose", "placement", "role", "size", "trigger"]);
    const [isOpenInternal, setIsOpenInternal] = useState(isOpen !== null && isOpen !== void 0 ? isOpen : false);
    const [activeIndex, setActiveIndex] = useState(null);
    const hasParentModal = useHasParentModal();
    const uuid = useId();
    const id = (_b = restProps.id) !== null && _b !== void 0 ? _b : `dropdown-${uuid}`;
    const triggerId = (_c = trigger.props.id) !== null && _c !== void 0 ? _c : `dropdown-trigger-${uuid}`;
    const listItemsRef = useRef([]); // Refs for all dropdown Items
    const middleware = [floatingOffset(parseInt(offset, 10)), flip()];
    const isListbox = role === 'listbox';
    // Set width of dropdown to match trigger, if fullWidth
    if (fullWidth) {
        middleware.push(floatingSize({
            apply({ elements, rects }) {
                // Only update width if it's above the min-width
                if (rects.reference.width > DROPDOWN_MIN_WIDTH) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`,
                    });
                }
            },
        }));
    }
    // Handle setting activeIndex to 0 for listbox functionality
    const handleOpenChange = useCallback((open) => {
        if (isListbox && open) {
            setActiveIndex(disableFirstItemAutoselect ? null : 0);
        }
        if (isOpen === undefined) {
            setIsOpenInternal(open);
        }
        else if (open === false) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
    }, [disableFirstItemAutoselect, isListbox, onClose, isOpen]);
    useEffect(() => {
        if (isOpen !== undefined) {
            setIsOpenInternal(isOpen);
        }
    }, [handleOpenChange, isOpen]);
    const { context, refs, x, y } = useFloating({
        middleware,
        onOpenChange: handleOpenChange,
        open: isOpenInternal,
        placement: placementMap[placement],
        whileElementsMounted: autoUpdate,
    });
    // Workaround for triggering the provided click handler on the Dropdown.Item, when it's an option / listbox
    function handleKeyDown(event) {
        var _a;
        if (event.key === 'Enter' && activeIndex != null) {
            (_a = listItemsRef.current[activeIndex]) === null || _a === void 0 ? void 0 : _a.click();
        }
    }
    const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions([
        useRole(context, { role }),
        useClick(context, Object.assign(Object.assign({}, (isListbox && { keyboardHandlers: false })), { enabled: isOpen === undefined })),
        useDismiss(context),
        useListNavigation(context, Object.assign(Object.assign(Object.assign({}, (disableFirstItemAutoselect &&
            isListbox && { focusItemOnOpen: false })), { activeIndex, listRef: listItemsRef, loop: true, onNavigate: setActiveIndex, openOnArrowKeyDown: !disableOpenOnArrowKeyDown }), (isListbox && { virtual: true }))),
    ]);
    // Enhance trigger with a11y aria attributes
    const element = cloneElement(trigger, mergeProps(getReferenceProps(Object.assign(Object.assign({ 'aria-haspopup': role, id: triggerId, key: trigger.key, 
        // @ts-expect-error: trigger.ref not recognized
        ref: mergeRefs(refs.setReference, trigger.ref) }, (isListbox && {
        onKeyDown: handleKeyDown,
    })), (inverted && { inverted }))), trigger.props));
    function handleTabOut(event) {
        var _a;
        // When Tab key is pressed close dropdown and return focus to trigger element
        if (event.key === 'Tab') {
            setIsOpenInternal(false);
            // @ts-expect-error: focus() not recognized
            (_a = refs.reference.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }
    return (_jsxs(DropdownProvider, Object.assign({}, {
        activeIndex,
        getItemProps,
        id,
        isListbox,
        isOpen: isOpenInternal,
        setIsOpen: handleOpenChange,
        size,
        triggerRef: refs.reference,
    }, { children: [element, _jsx(Portal, Object.assign({ id: customPortalId }, { children: isOpenInternal && (_jsx(FloatingFocusManager, Object.assign({ context: context, initialFocus: -1 }, { children: _jsx("div", Object.assign({ css: dropdownStyle({
                            hasParentModal,
                            inverted,
                            x,
                            y,
                        }) }, getFloatingProps(Object.assign(Object.assign({ ref: refs.setFloating }, restProps), { id, onKeyDown: handleTabOut })), { "aria-labelledby": triggerId, role: role }, { children: _jsx(FloatingList, Object.assign({ elementsRef: listItemsRef }, { children: children })) })) }))) }))] })));
}
export default DropdownInternal;
